import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import PolygonSvg from '../../../assets/images/svgs/Polygon.svg?react';
import PointSvg from '../../../assets/images/svgs/Point.svg?react';
import RectangleSvg from '../../../assets/images/svgs/Rectangle.svg?react';
import CircleSvg from '../../../assets/images/svgs/CircleDraw.svg?react';
import DeleteSvg from '../../../assets/images/svgs/Delete.svg?react';
import UploadSvg from '../../../assets/images/svgs/Upload.svg?react';
import SaveSvg from '../../../assets/images/svgs/Save.svg?react';
import ExplorerSvg from '../../../assets/images/svgs/Explorer.svg?react';
import DateIcon from '../../../assets/images/svgs/Date.svg?react';
import DataType from '../../../assets/images/svgs/DataType.svg?react';

import * as mapActions from "../../../slices/mapSlice";
import { updateFilteredCollections, updateDateRange } from "../../../slices/filtersSlice";
import { searchMultiStacData, searchStacData } from "../../../services/stac.service";
import { resetUploadedFeatures, setUploadedFeatures } from "../../../slices/orderSlice";
import { handleFileUpload, formatDateOnly } from "../../../common/utils/helperFunctions";
import MultiSelectDropdown from "../../../common/utils/MultiSelectDropdown";
import DateTimeRangePicker from "../../../common/utils/DateTimeRangePicker";
import { Tooltip } from "react-tooltip";

const FeaturesBar = ({ props }) => {

  const isOrderCreator = props?.fromOrderCreator;

  const dispatch = useDispatch();
  const mode = useSelector(state => state.map.mode);
  const displayView = useSelector(state => state.map.displayView);
  const headerHeight = useSelector(state => state.map.headerHeight);
  const analysisMode = useSelector(state => state.map.analysisMode);
  const multicoordinates = useSelector(state => state.map.multicoordinates);
  const filters = useSelector(state => state.filters);
  const collectionsData = useSelector(state => state.collectionDetails.collections);

  const [selectedCollections, setSelectedCollections] = useState(
    filters.filteredCollections?.length > 0
      ? filters.filteredCollections
      : (filters.filteredCollection?.collectionID ? [filters.filteredCollection] : [])
  );
  const [startDate, setStartDate] = useState(filters.startDate);
  const [endDate, setEndDate] = useState(filters.endDate);

  const collectionsWithStats = selectedCollections.filter(
    (item) => item.firstItemDatetime && item.lastItemDatetime
  );
  const earliestAvailableDate = collectionsWithStats.length > 0
    ? collectionsWithStats.reduce((min, item) => item.firstItemDatetime < min ? item.firstItemDatetime : min, collectionsWithStats[0].firstItemDatetime)
    : null;
  const latestAvailableDate = collectionsWithStats.length > 0
    ? collectionsWithStats.reduce((max, item) => item.lastItemDatetime > max ? item.lastItemDatetime : max, collectionsWithStats[0].lastItemDatetime)
    : null;

  const onCollectionsChange = (selectedItems) => {
    setSelectedCollections(selectedItems);
    dispatch(updateFilteredCollections(selectedItems));
  };

  useEffect(() => {
    if (earliestAvailableDate && latestAvailableDate) {
      setStartDate(earliestAvailableDate);
      setEndDate(latestAvailableDate);
    }
  }, [earliestAvailableDate, latestAvailableDate]);

  useEffect(() => {
    if (startDate && endDate) {
      dispatch(updateDateRange({ startDate, endDate }));
    }
  }, [startDate, endDate]);

  const cleanUpDrawnFeatures = () => {
    dispatch(mapActions.resetCoordinatesGeometry());
    dispatch(resetUploadedFeatures());
    dispatch(mapActions.cleanUpDraw());
  };

  const drawFeatures = (state) => {
      dispatch(mapActions.drawAOI(state.map.mode));
    };
  const drawMultiFeatures = (state) => {
    dispatch(mapActions.drawMultiAOI(state.map.mode));
  };

  const selectAction = (action) => {
    if (action === 'upload_file' || action === 'delete_aoi') {
      dispatch(mapActions.selectMode(action, cleanUpDrawnFeatures));
    } else if (!analysisMode) {
      dispatch(mapActions.selectMode(action, drawFeatures));
    }
    else{
      dispatch(mapActions.selectMode(action, drawMultiFeatures));

    }
  };

  const handleSave = () => {
    dispatch(mapActions.resetAnalysisMode());
    dispatch(mapActions.setMultiSearchView());
    if (multicoordinates && Array.isArray(multicoordinates.features)) {
      multicoordinates.features.forEach((feature,index) => {
        const geometry = feature.geometry;
        const name = `${feature.geometry.type}-${index + 1}`;
        if (geometry && geometry.type && geometry.coordinates) {
          dispatch(searchMultiStacData(geometry,name));
        }
      });
    }
    dispatch(mapActions.cleanUpDraw());
    dispatch(mapActions.resetMode());
    dispatch(mapActions.resetDisplayView());
  };

  const onFileUpload = (fileInfo) => {
    if (analysisMode) {
      dispatch(mapActions.setMultiAOICoordinates(fileInfo.geojson));
      dispatch(setUploadedFeatures(fileInfo));
    } else {
      const geometry = fileInfo.geojson.features[0].geometry;
      dispatch(setUploadedFeatures(fileInfo));
      dispatch(mapActions.setGeometryDrawn(geometry));

      if (isOrderCreator) {
        return;
      }

      dispatch(mapActions.setSearchedAoi(geometry));
      dispatch(mapActions.setSearchView());
      dispatch(searchStacData());
      dispatch(mapActions.cleanUpDraw());
      dispatch(mapActions.resetMode());
      dispatch(mapActions.resetDisplayView());
    }
  };

  return (
    <div className={isOrderCreator ? "featuresBar" : "featuresBar exploreBar"}>

      {!isOrderCreator &&
        <>
          <div className="popupBoxDiv" style={{ padding: "4px" }}>
            <ExplorerSvg />
            <div className="popupBoxText popupBoxHeaderText">Explore</div>
          </div>

          <div className="noPadding">
            <div className="dataTypeFilters">
              <div className="fc-daygrid-dot-event flex-row">
                <DataType />
                <div className="collection-dropdown-title">Collection</div>
              </div>
            </div>

            <MultiSelectDropdown
              options={collectionsData}
              onSelectionChange={onCollectionsChange}
              selectedValues={selectedCollections}
              keyName="collectionID"
              valueName="name"
              dropdownName="collections"
              placeholder="Select collection(s)"
              itemLabel="collection"
              toggleClass="dropdown-toggle-width"
              menuClass="dropdown-menu-width"
            />

            <div>
              <div className="fc-daygrid-dot-event flex-row">
                <DateIcon />
                <div className="collection-dropdown-title">Date</div>
              </div>
              {earliestAvailableDate && (
                <div className="collectionStatsHint">
                  <div>Data available:</div>
                  <div>{formatDateOnly(earliestAvailableDate)} &ndash; {formatDateOnly(latestAvailableDate)}</div>
                </div>
              )}
              <DateTimeRangePicker
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                minDate={earliestAvailableDate}
                maxDate={latestAvailableDate}
              />
            </div>
          </div>
        </>
      }

      <div className="drawOptions">
        <button
          className={mode === 'draw_polygon' ? "draw-item active" : "draw-item"}
          onClick={() => { selectAction('draw_polygon') }}
          data-tooltip-id="draw_polygon"
          data-tooltip-content="Polygon"
          data-tooltip-place="bottom"
        >
          <PolygonSvg />
        </button>
        <Tooltip id="draw_polygon" />
        <button
          className={mode === 'draw_point' ? "draw-item active" : "draw-item"}
          onClick={() => { selectAction('draw_point') }}
          data-tooltip-id="draw_point"
          data-tooltip-content="Point"
          data-tooltip-place="bottom"
        >
          <PointSvg />
        </button>
        <Tooltip id="draw_point" />
        <button
          className={mode === 'draw_rectangle' ? "draw-item active" : "draw-item"}
          onClick={() => { selectAction('draw_rectangle') }}
          data-tooltip-id="draw_rectangle"
          data-tooltip-content="Rectangle"
          data-tooltip-place="bottom"
        >
          <RectangleSvg />
        </button>
        <Tooltip id="draw_rectangle" />
        <button
          className={mode === 'drag_circle' ? "draw-item active" : "draw-item"}
          onClick={() => { selectAction('drag_circle') }}
          data-tooltip-id="drag_circle"
          data-tooltip-content="Circle"
          data-tooltip-place="bottom"
        >
          <CircleSvg />
        </button>
        <Tooltip id="drag_circle" />
        <button
          className={mode === 'upload_file' ? "draw-item active" : "draw-item"}
          onClick={() => { selectAction('upload_file') }}
          data-tooltip-id="upload_file"
          data-tooltip-content="Upload"
          data-tooltip-place="bottom"
        >
          <input
            id="upload"
            name="uploadFile"
            type="file"
            accept=".zip,.geojson,.json,.topojson,.kml,.wkt"
            onChange={(e) => handleFileUpload(e, onFileUpload)}
            style={{ display: 'none' }}
          />
          <label
            className="uploadIcon"
            htmlFor="upload">
            <UploadSvg />
          </label>
        </button>
        <Tooltip id="upload_file" />
        <button
          className={mode === 'delete_aoi' ? "draw-item active" : "draw-item"}
          onClick={() => { selectAction('delete_aoi') }}
          data-tooltip-id="delete_aoi"
          data-tooltip-content="Delete"
          data-tooltip-place="bottom"
        >
          <DeleteSvg />
        </button>
        <Tooltip id="delete_aoi" />
      </div>

      {displayView === 'drawView' && analysisMode &&
        <div className="drawOptions">
          <button
            className="draw-item"
            onClick={handleSave}
            data-tooltip-id="save-btn"
            data-tooltip-content="Save"
            data-tooltip-place="bottom"
          >
            <SaveSvg />
          </button>
          <Tooltip id="save-btn" />
        </div>
      }
    </ div>
  );
};

export default FeaturesBar;
